<template>
	<div :class="{ upgrade: upgrade }">
		<div class="flavor" v-for="(option, index) in data" :key="index">
			<span>
				{{ option.name }}
				<span class="tag" v-if="option.required">*</span>
				<span class="quantityText" :class="{ active: option.required }">
					{{ getQuantityIndicates(option) }}
				</span>
			</span>
			<div
				class="errorValidate"
				v-if="option.errorValidate && option.errorMessage"
			>
				{{ option.errorMessage }}
			</div>
			<div
				class="flavor-option"
				v-for="(optionDetail, o_index) in option.optionDetailList"
				:key="'options-' + o_index"
			>
				<template v-if="canBuy">
					<ByRadio
						v-if="!option.type"
						:name="String(optionDetail.id)"
						:title="optionDetail.name"
						:required="option.required"
						:disabled="disabledRadioOption(option, optionDetail)"
						:class="{
							'sold-out-selected': isSoldOutSelected(option, optionDetail),
						}"
						v-model="option.count"
					>
						<span v-if="isSoldOut(optionDetail)" class="flavor-soldout"
							>售完</span
						>
						<span v-else class="flavor-price">
							{{
								optionDetail.price
									? `+$ ${$stepPrice(optionDetail.price * optionDetail.count)}`
									: ''
							}}
						</span>
					</ByRadio>
					<ByCheckbox
						v-else
						:name="String(optionDetail.id)"
						:value="option.count.includes(String(optionDetail.id))"
						:title="optionDetail.name"
						@change="handleChange(index, String(optionDetail.id))"
						:class="{
							disabled: disabledOption(option, optionDetail),
							'sold-out-selected': isSoldOutSelected(option, optionDetail),
						}"
						:disabled="disabledOption(option, optionDetail)"
					>
						<span v-if="isSoldOut(optionDetail)" class="flavor-soldout"
							>售完</span
						>
						<span
							v-else
							class="flavor-price"
							:class="{ hide: !optionDetail.price }"
						>
							+$ {{ $stepPrice(optionDetail.price * optionDetail.count) }}
						</span>
					</ByCheckbox>
				</template>
				<div v-else>
					{{ optionDetail.name }}
					<span v-if="isSoldOut(optionDetail)" class="flavor-soldout"
						>售完</span
					>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import ByCheckbox from '@/components/Checkbox.vue'

export default {
	name: 'Flavor',
	components: { ByCheckbox },
	props: {
		// 這個是optionList
		data: {
			type: Array,
			default: () => [],
		},
		upgrade: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {}
	},
	computed: {
		canBuy() {
			return this.$store.getters.canBuy
		},
	},
	methods: {
		handleChange(index, optionId) {
			const selectOptionIds = this.data[index].count
			const selectedIndex = selectOptionIds.findIndex(e => e === optionId)
			const hasOption = selectedIndex !== -1
			if (hasOption) {
				selectOptionIds.splice(selectedIndex, 1)
			} else {
				selectOptionIds.push(optionId)
			}
		},
		// 口味數量標示
		getQuantityIndicates(option) {
			// 單選
			if (!option.type && option.required) {
				return '必選1'
			}

			// 複選
			if (option.type) {
				const { leastQuantity, mostQuantity } = option
				// 上下限相同
				if (leastQuantity === mostQuantity) {
					return `必選${leastQuantity}`
				}
				// 有設置下限
				else if (Number(leastQuantity) > 0) {
					// 判斷上限的顯示文字
					const maximumText = mostQuantity === null ? '不限' : mostQuantity
					return `最少${leastQuantity}最多${maximumText}`
				}
				// 下限為 0 = 沒設，不顯示下限
				else if (Number(leastQuantity) === 0 && mostQuantity !== null) {
					return `最多選${mostQuantity}`
				}

				// 上下限都沒設不顯示
			}
		},
		// 口味細項是否售完（fail-open：欄位未落地時視為可售）
		isSoldOut(optionDetail) {
			return optionDetail.availableForSale === false
		},
		// 已選中的售完細項：刻意不禁用（保留取消能力），改以文字淡化與可售項區隔；
		// 不沿用 disabled 的整體 opacity，否則看起來與點不動的售完項無異
		isSoldOutSelected(option, optionDetail) {
			if (!this.isSoldOut(optionDetail)) {
				return false
			}
			return option.type
				? Array.isArray(option.count) &&
						option.count.includes(String(optionDetail.id))
				: String(option.count) === String(optionDetail.id)
		},
		// 售完僅禁止「尚未選取」者；已選中的售完項保持可點，
		// 讓非必選口味能再次點擊清空（見 Radio.vue onClick），否則驗證會持續卡住
		disabledRadioOption(option, optionDetail) {
			return (
				this.isSoldOut(optionDetail) &&
				String(option.count) !== String(optionDetail.id)
			)
		},
		// 口味細項是否被禁用
		disabledOption(option, optionDetail) {
			// 售完僅禁止新勾選；已勾選者保持可取消，避免驗證卡死
			if (
				this.isSoldOut(optionDetail) &&
				!option.count.includes(String(optionDetail.id))
			) {
				return true
			}
			const { mostQuantity } = option
			// 沒有設定上限就不用禁用
			if (!mostQuantity) {
				return false
			}

			// 已選數量排除「已售完的已選項」（與加入時的數量驗證一致），
			// 讓使用者不必先取消售完項即可改選其他細項
			const checkedCount = (
				Array.isArray(option.count) ? option.count : []
			).filter(id => {
				const selectedDetail = option.optionDetailList?.find(
					detail => String(detail.id) === String(id),
				)
				// 最新清單查無此細項（已被移除）→ 不計入已選數量，
				// 否則這個看不見也點不掉的 id 會一直佔用上限
				if (!selectedDetail) {
					return false
				}
				return !this.isSoldOut(selectedDetail)
			}).length

			// 是否已抵達上限
			const reachedMost = checkedCount >= mostQuantity

			// 是否為已選選項 (已選本身不能被禁用)
			const include = option.count.includes(String(optionDetail.id))

			return reachedMost && !include
		},
	},
	// created() {},
	// mounted() {},
	// beforeDestroy () {},
}
</script>

<style lang="sass" scoped>
.flavor
	display: block
	width: calc(100% - 40px)
	padding: 20px 0
	margin: 0 auto
	border-bottom: 1px solid #D8D8D8
	position: relative

	&-name
		font-size: 16px
		font-weight: bold
		display: flex
		align-items: center
		position: relative

	&-tag
		margin: 10px 0


	&-required
		&--must
			padding: 0 0px 0 5px
			color: red
			font-weight: bold
			font-size: 13px
			transform: translate(0px, -6px)


		&--not
			font-weight: normal
			margin-left: 5px
			font-size: 14px
			padding: 4px
			border-radius: 4px
			background: #F3F3F3
			color: #434343

	.tag
		margin: 0px 0
		font-size: 12.5px
		padding: 0 0px 0 5px
		color: red
		position: absolute

	.quantityText
		color: gray
		margin-left: 5px
		font-size: 12.5px
		font-weight: normal

		&.active
			color: red
			margin-left: 10px

	&-option
		padding-top: 10px
		color: #434343

	// 已選中的售完細項：控制項維持可互動外觀，只淡化文字
	.sold-out-selected ::v-deep .title
		color: #999

	&-price
		min-width: 45px
		text-align: right

	&-soldout
		min-width: 45px
		text-align: right
		color: #999
		font-size: 14px

	&-control
		display: flex
		align-items: center
		justify-content: space-between
		width: 110px
		font-weight: bold

.upgrade
	margin: 0px 20px 20px 20px

	/*border: 1px dashed rgba(0,0,0,0.2)*/
	background: rgba(0, 0, 0, 0.03)

	.flavor

	.flavor-name
		font-size: 16px

	&::v-deep
		.radio
			min-width: 22px
			width: 22px
			height: 22px

		.title, .flavor-price, .number
			font-size: 16px

		input[type="radio"]:checked
			& + .radio
				&:before
					width: 12px
					height: 12px

		.by-checkbox .checkbox
			min-width: 22px
			width: 22px
			height: 22px

		.by-checkbox input[type="checkbox"]:checked
			& + .checkbox

				&:before
					content: ''
					border: solid #fff
					border-width: 0 3px 3px 0
					padding: 3px
					margin-bottom: 3px
					transform: rotate(45deg)

		.by-checkbox > label, .by-radio > label
			width: 50%

		.amount
			width: 85px

			.number
				width: 35px

			.minus, .plus
				width: 25px


.errorValidate
	font-size: 14px
	color: red
	margin-top: 5px
</style>
